import { EventEmitter } from '../utils/event-emitter.js';
import {
  querySelector,
  createElement,
  formatDuration,
  textContains,
  escapeHtml,
  animateElement,
} from '../utils/helpers.js';
import {
  SELECTORS,
  CSS_CLASSES,
  SORT_TYPES,
  SORT_ORDERS,
  EVENTS,
  ANIMATION_CONFIG,
  DATA_ATTRIBUTES,
} from '../utils/constants.js';

/**
 * Менеджер фильмов
 * Отвечает за отображение, фильтрацию, поиск и сортировку фильмов
 */
export class MovieManager extends EventEmitter {
  constructor() {
    super();
    this.movies = [];
    this.container = null;
    this.noResultsElement = null;
  }

  /**
   * Инициализация менеджера
   * @param {Array} movies - Массив фильмов
   */
  init(movies) {
    this.movies = movies;
    this.container = querySelector(SELECTORS.MOVIES_CONTAINER);
    this.noResultsElement = querySelector(SELECTORS.NO_RESULTS);

    if (!this.container) {
      throw new Error('Контейнер для фильмов не найден');
    }

    console.log(`🎬 MovieManager инициализирован с ${movies.length} фильмами`);
  }

  /**
   * Фильтрация фильмов по категориям
   * @param {Array} movies - Исходный массив фильмов
   * @param {Array} categories - Выбранные категории
   * @returns {Array} - Отфильтрованные фильмы
   */
  filterByCategories(movies, categories) {
    if (!categories || categories.length === 0) {
      return movies;
    }

    return movies.filter(movie => categories.includes(movie.category));
  }

  /**
   * Поиск по фильмам
   * @param {Array} movies - Исходный массив фильмов
   * @param {string} query - Поисковый запрос
   * @returns {Array} - Найденные фильмы
   */
  search(movies, query) {
    if (!query || query.trim().length === 0) {
      return movies;
    }

    const searchQuery = query.trim();

    return movies.filter(movie => {
      // Поиск по названию
      if (textContains(movie.title, searchQuery)) return true;

      // Поиск по описанию
      if (textContains(movie.description, searchQuery)) return true;

      // Поиск по тегам
      if (movie.tags && movie.tags.some(tag => textContains(tag, searchQuery))) return true;

      // Поиск по продолжительности (если введено число)
      const duration = parseInt(searchQuery, 10);
      if (!isNaN(duration) && movie.duration === duration) return true;

      return false;
    });
  }

  /**
   * Сортировка фильмов
   * @param {Array} movies - Исходный массив фильмов
   * @param {string} sortType - Тип сортировки
   * @param {string} sortOrder - Порядок сортировки
   * @param {Object} categories - Объект категорий
   * @returns {Array} - Отсортированные фильмы
   */
  sort(movies, sortType, sortOrder, categories) {
    const sortedMovies = [...movies];

    switch (sortType) {
    case SORT_TYPES.ALPHABET:
      sortedMovies.sort((a, b) => a.title.localeCompare(b.title, 'ru'));
      break;

    case SORT_TYPES.CATEGORY:
      sortedMovies.sort((a, b) => {
        const categoryA = categories[a.category]?.name || a.category;
        const categoryB = categories[b.category]?.name || b.category;
        return categoryA.localeCompare(categoryB, 'ru');
      });
      break;

    case SORT_TYPES.DURATION:
      sortedMovies.sort((a, b) => a.duration - b.duration);
      break;

    default:
      sortedMovies.sort((a, b) => a.title.localeCompare(b.title, 'ru'));
    }

    if (sortOrder === SORT_ORDERS.DESC) {
      sortedMovies.reverse();
    }

    return sortedMovies;
  }

  /**
   * Рендеринг списка фильмов
   * @param {Array} movies - Массив фильмов для отображения
   * @param {Object} categories - Объект категорий
   */
  render(movies, categories) {
    if (!this.container) return;

    // Очищаем контейнер
    this.container.innerHTML = '';

    if (movies.length === 0) {
      this.showNoResults(true);
      return;
    }

    this.showNoResults(false);

    // Создаем карточки фильмов с анимацией
    movies.forEach((movie, index) => {
      const movieCard = this.createMovieCard(movie, categories);
      this.container.appendChild(movieCard);

      // Анимация появления с задержкой
      animateElement(
        movieCard,
        CSS_CLASSES.FADE_IN,
        index * ANIMATION_CONFIG.STAGGER_DELAY,
      );
    });

    // Уведомляем о завершении рендеринга
    this.emit(EVENTS.MOVIES_RENDERED, movies);
  }

  /**
   * Создание карточки фильма
   * @param {Object} movie - Данные фильма
   * @param {Object} categories - Объект категорий
   * @returns {Element} - DOM элемент карточки
   */
  createMovieCard(movie, categories) {
    const category = categories[movie.category];
    const categoryName = category?.name || movie.category;
    const categoryCssClass = category?.cssClass || movie.category;

    const card = createElement('div', {
      className: `${CSS_CLASSES.MOVIE_CARD} ${CSS_CLASSES.MOVIE_CARD_PREFIX}${categoryCssClass}`,
      [DATA_ATTRIBUTES.MOVIE_ID]: movie.id,
    });

    // Путь к постеру
    const posterPath = `images/posters/${movie.id}.png`;

    // Постер фильма
    const poster = createElement('div', {
      className: 'movie-poster',
    });

    // Создаем изображение
    const img = createElement('img', {
      src: posterPath,
      alt: movie.title,
      className: 'movie-poster-image',
      loading: 'lazy',
    });

    // Обработчики загрузки изображения
    img.addEventListener('load', () => {
      img.classList.add('loaded');
    });

    img.addEventListener('error', () => {
      // Если изображение не загрузилось, показываем fallback
      poster.innerHTML = '';
      const fallback = createElement('div', {
        className: `movie-poster-fallback poster-fallback-${categoryCssClass}`,
      }, escapeHtml(categoryName));
      poster.appendChild(fallback);
    });

    poster.appendChild(img);

    // Информация о фильме
    const info = createElement('div', {
      className: 'movie-info',
    });

    // Название фильма
    const title = createElement('div', {
      className: 'movie-title',
    }, escapeHtml(movie.title));

    // Метаданные
    const meta = createElement('div', {
      className: 'movie-meta',
    });

    // Категория
    const categoryElement = createElement('div', {
      className: 'movie-category',
    }, escapeHtml(categoryName));

    // Продолжительность
    const duration = createElement('div', {
      className: 'movie-duration',
    }, `
      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.2 14.2L11 13V7h1.5v5.2l4.5 2.7-.8 1.3z"/>
      </svg>
      ${formatDuration(movie.duration)}
    `);

    // Описание
    const description = createElement('div', {
      className: 'movie-description',
    }, escapeHtml(movie.description));

    // Сборка карточки
    meta.appendChild(categoryElement);
    meta.appendChild(duration);

    info.appendChild(title);
    info.appendChild(meta);
    info.appendChild(description);

    card.appendChild(poster);
    card.appendChild(info);

    return card;
  }

  /**
   * Показать/скрыть сообщение "Нет результатов"
   * @param {boolean} show - Показать или скрыть
   */
  showNoResults(show) {
    if (!this.noResultsElement) return;

    if (show) {
      this.noResultsElement.classList.remove(CSS_CLASSES.HIDDEN);
    } else {
      this.noResultsElement.classList.add(CSS_CLASSES.HIDDEN);
    }
  }

  /**
   * Получение фильма по ID
   * @param {number} id - ID фильма
   * @returns {Object|null} - Найденный фильм
   */
  getMovieById(id) {
    return this.movies.find(movie => movie.id === parseInt(id, 10)) || null;
  }

  /**
   * Получение всех фильмов определенной категории
   * @param {string} category - ID категории
   * @returns {Array} - Фильмы категории
   */
  getMoviesByCategory(category) {
    return this.movies.filter(movie => movie.category === category);
  }

  /**
   * Получение статистики по фильмам
   * @returns {Object} - Статистика
   */
  getStatistics() {
    const stats = {
      total: this.movies.length,
      byCategory: {},
      avgDuration: 0,
      totalDuration: 0,
    };

    // Подсчет по категориям и общей продолжительности
    this.movies.forEach(movie => {
      const {category} = movie;
      stats.byCategory[category] = (stats.byCategory[category] || 0) + 1;
      stats.totalDuration += movie.duration || 0;
    });

    // Средняя продолжительность
    if (stats.total > 0) {
      stats.avgDuration = Math.round(stats.totalDuration / stats.total);
    }

    return stats;
  }

  /**
   * Очистка контейнера
   */
  clear() {
    if (this.container) {
      this.container.innerHTML = '';
    }
  }

  /**
   * Деструктор
   */
  destroy() {
    this.clear();
    this.removeAllListeners();
    this.movies = [];
    this.container = null;
    this.noResultsElement = null;
  }
}
